import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ExerciseScoreService } from '../../services/ExerciseScoreService';
import { ChunkyButton, Confetti, ComboPill, StarRow, FloatingXpBadge } from '../../components/GameUI';

// Slice-the-noun arcade game — port of the mobile app's FruitSliceScreen.
// Words launch upward in a parabolic arc; the player slices (drags/taps
// across) the nouns and must avoid everything else. Driven by a
// requestAnimationFrame loop that mirrors the mobile screen's raw Ticker:
// game state lives in refs so the loop's math never sees a stale closure,
// and a `frame` counter forces a React re-render every tick (~60fps), the
// same way the Dart version calls setState() on every tick.

const FRUIT_EMOJIS = ['🍎', '🍊', '🍇', '🍓', '🍉', '🍌', '🍑', '🥝', '🍍', '🥭'];
const TARGET_NOUNS = 12;
const START_LIVES = 3;
const HIT_RADIUS = 42;
const SLOW_MO_MS = 1300;

function spawnFruit(nouns, others, elapsedMs) {
  const wantNoun = Math.random() < 0.55;
  const source = wantNoun && nouns.length
    ? nouns[Math.floor(Math.random() * nouns.length)]
    : (others.length ? others[Math.floor(Math.random() * others.length)] : nouns[Math.floor(Math.random() * nouns.length)]);
  return {
    id: `${source.id}-${Math.floor(elapsedMs)}-${Math.floor(Math.random() * 9999)}`,
    source,
    emoji: FRUIT_EMOJIS[Math.floor(Math.random() * FRUIT_EMOJIS.length)],
    x0: 0.1 + Math.random() * 0.8,
    drift: (Math.random() - 0.5) * 0.3,
    peakFrac: 0.55 + Math.random() * 0.3,
    spawnMs: elapsedMs,
    flightMs: 2000 + Math.random() * 600,
    resolved: false,
  };
}

function positionOf(f, elapsedMs, width, height) {
  const t = Math.max(0, Math.min(1, (elapsedMs - f.spawnMs) / f.flightMs));
  const x = (f.x0 + f.drift * t) * width;
  const y = height - height * f.peakFrac * 4 * t * (1 - t);
  return { x, y, t };
}

export default function FruitSliceGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    nouns = [], others = [],
    targetLang = { code: '', label: '', dir: 'ltr' },
    langPair = 'guest',
  } = state || {};

  const containerRef = useRef(null);

  // Game-loop-critical numbers live in refs, mirroring the Dart Ticker
  // approach — read directly in render, driven by the `frame` tick below.
  const elapsedMsRef = useRef(0);
  const nextSpawnMsRef = useRef(0);
  const lastTimeRef = useRef(null);
  const timeScaleRef = useRef(1);
  const fruitsRef = useRef([]);
  const nounsSlicedRef = useRef(0);
  const wrongSlicesRef = useRef(0);
  const livesRef = useRef(START_LIVES);
  const comboRef = useRef(0);
  const maxComboRef = useRef(0);
  const rewardedRef = useRef(false);
  const doneRef = useRef(false);

  const [, setFrame] = useState(0);
  const [done, setDone] = useState(false);
  const [slowMo, setSlowMo] = useState(false);
  const [comboPopKey, setComboPopKey] = useState(0);
  const [particles, setParticles] = useState([]);
  const [trail, setTrail] = useState([]);
  const [playSize, setPlaySize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!nouns.length && !others.length) navigate('/games', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Measure the play area so fruit positions can be computed in pixels.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setPlaySize({ width: r.width, height: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function xpEarned() {
    const stars = starsFor();
    return nounsSlicedRef.current * 4 + (stars === 3 ? 10 : 0);
  }

  function percentFor() {
    if (nounsSlicedRef.current >= TARGET_NOUNS) {
      return Math.max(0, Math.min(100, 100 - wrongSlicesRef.current * 15));
    }
    return Math.round((nounsSlicedRef.current / TARGET_NOUNS) * 100);
  }

  function starsFor() {
    return ExerciseScoreService.starsForPercent(percentFor());
  }

  function finish() {
    if (doneRef.current) return;
    doneRef.current = true;
    setDone(true);
    if (rewardedRef.current) return;
    rewardedRef.current = true;
    const stars = starsFor();
    StreakService.recordActivity();
    XpService.addXp(xpEarned());
    if (stars === 3) SoundEffectService.playCelebration();
  }

  function triggerSlowMo() {
    timeScaleRef.current = 0.35;
    setSlowMo(true);
    setTimeout(() => {
      if (!doneRef.current) {
        timeScaleRef.current = 1;
        setSlowMo(false);
      }
    }, SLOW_MO_MS);
  }

  function spawnParticle(x, y, success) {
    const p = { id: `${Date.now()}-${Math.random()}`, x, y, success };
    setParticles((cur) => [...cur, p]);
    setTimeout(() => {
      setParticles((cur) => cur.filter((q) => q.id !== p.id));
    }, 450);
  }

  function sliceFruit(f, x, y) {
    f.resolved = true;
    if (f.source.isNoun) {
      spawnParticle(x, y, true);
      SoundEffectService.playCorrect();
      if (navigator.vibrate) navigator.vibrate(15);
      nounsSlicedRef.current += 1;
      comboRef.current += 1;
      if (comboRef.current > maxComboRef.current) maxComboRef.current = comboRef.current;
      if (comboRef.current >= 2) setComboPopKey((k) => k + 1);
      if (comboRef.current > 0 && comboRef.current % 3 === 0) triggerSlowMo();
      LearningProgressService.record(langPair, 'vocabulary', 1, { activityId: `fruitslice:${f.source.id}` });
      if (nounsSlicedRef.current >= TARGET_NOUNS) {
        finish();
      }
    } else {
      spawnParticle(x, y, false);
      SoundEffectService.playWrong();
      if (navigator.vibrate) navigator.vibrate(35);
      wrongSlicesRef.current += 1;
      comboRef.current = 0;
      livesRef.current -= 1;
      if (livesRef.current <= 0) {
        finish();
      }
    }
  }

  function trySliceAt(clientX, clientY) {
    if (doneRef.current) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    for (const f of fruitsRef.current) {
      if (f.resolved) continue;
      const pos = positionOf(f, elapsedMsRef.current, rect.width, rect.height);
      const dx = pos.x - x;
      const dy = pos.y - y;
      if (Math.sqrt(dx * dx + dy * dy) <= HIT_RADIUS) {
        sliceFruit(f, pos.x, pos.y);
        break;
      }
    }
  }

  // Main loop — spawns fruit, advances/expires flight paths, and forces a
  // re-render every frame, exactly like the Dart Ticker's onTick.
  useEffect(() => {
    if (done) return undefined;
    let rafId;
    function loop(now) {
      if (lastTimeRef.current == null) lastTimeRef.current = now;
      const dtMs = Math.max(0, Math.min(100, now - lastTimeRef.current));
      lastTimeRef.current = now;
      if (!doneRef.current) {
        elapsedMsRef.current += dtMs * timeScaleRef.current;

        if (elapsedMsRef.current >= nextSpawnMsRef.current && playSize.width > 0) {
          fruitsRef.current.push(spawnFruit(nouns, others, elapsedMsRef.current));
          const interval = Math.max(420, Math.min(900, 900 - nounsSlicedRef.current * 15));
          nextSpawnMsRef.current = elapsedMsRef.current + interval;
        }

        fruitsRef.current = fruitsRef.current.filter((f) => {
          if (f.resolved) return false;
          const t = (elapsedMsRef.current - f.spawnMs) / f.flightMs;
          return t < 1;
        });

        setFrame((n) => n + 1);
      }
      rafId = requestAnimationFrame(loop);
    }
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, playSize.width]);

  function handlePointerDown(e) {
    if (containerRef.current?.setPointerCapture && e.pointerId != null) {
      try { containerRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }
    trySliceAt(e.clientX, e.clientY);
    setTrail((cur) => [...cur.slice(-7), { id: Math.random(), x: e.clientX, y: e.clientY }]);
  }

  function handlePointerMove(e) {
    if (e.buttons !== 1 && e.pointerType !== 'touch') return;
    trySliceAt(e.clientX, e.clientY);
    setTrail((cur) => [...cur.slice(-7), { id: Math.random(), x: e.clientX, y: e.clientY }]);
  }

  function handleTouchMove(e) {
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    trySliceAt(touch.clientX, touch.clientY);
    setTrail((cur) => [...cur.slice(-7), { id: Math.random(), x: touch.clientX, y: touch.clientY }]);
  }

  function restart() {
    fruitsRef.current = [];
    elapsedMsRef.current = 0;
    nextSpawnMsRef.current = 0;
    lastTimeRef.current = null;
    timeScaleRef.current = 1;
    nounsSlicedRef.current = 0;
    wrongSlicesRef.current = 0;
    livesRef.current = START_LIVES;
    comboRef.current = 0;
    maxComboRef.current = 0;
    rewardedRef.current = false;
    doneRef.current = false;
    setParticles([]);
    setTrail([]);
    setSlowMo(false);
    setDone(false);
    setFrame((n) => n + 1);
  }

  if (!nouns.length && !others.length) return null;

  const stars = starsFor();
  const perfect = stars === 3;
  const trailRect = !done ? containerRef.current?.getBoundingClientRect() : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex h-screen max-w-lg flex-col px-4 py-4 sm:px-6">
        <div className="mb-2 flex shrink-0 items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50"
          >
            ← {t('games.title')}
          </button>
          <h1 className="text-sm font-black text-slate-900">{t('games.fruitSlice')}</h1>
          <span className="w-16" />
        </div>

        {done ? (
          <div className="relative flex flex-1 flex-col items-center justify-center py-6 text-center">
            {perfect && <Confetti />}
            <span className="text-6xl">{perfect ? '🏆' : (livesRef.current <= 0 ? '💥' : '🎉')}</span>
            <h2 className="mt-3 text-2xl font-black text-slate-900">
              {t('games.fruitSliceComplete', 'Fruit Slice Complete!')}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t('games.score')}: {nounsSlicedRef.current}/{TARGET_NOUNS}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {t('games.wordMatchBestCombo', { combo: maxComboRef.current })}
            </p>
            <div className="mt-3">
              <StarRow count={stars} />
            </div>
            <div className="mt-3">
              <FloatingXpBadge xp={xpEarned()} />
            </div>
            <div className="mt-8 w-full max-w-xs">
              <ChunkyButton color="#4F46E5" onClick={restart} className="w-full py-4 text-center text-white">
                {t('games.tryAgain')}
              </ChunkyButton>
              <button
                onClick={() => navigate('/games')}
                className="mt-3 w-full rounded-2xl border border-slate-200 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                {t('games.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-1 flex shrink-0 items-center justify-between">
              <div className="flex items-center gap-0.5 text-lg">
                {Array.from({ length: START_LIVES }).map((_, i) => (
                  <span key={i}>{i < livesRef.current ? '❤️' : '🤍'}</span>
                ))}
              </div>
              <span className="text-base font-black text-slate-900">
                {nounsSlicedRef.current}/{TARGET_NOUNS}
              </span>
              {comboRef.current >= 2 ? <ComboPill key={comboPopKey} combo={comboRef.current} /> : <span className="w-16" />}
            </div>
            <p className="mb-2 shrink-0 px-2 text-center text-xs font-semibold text-slate-400">
              {t('games.fruitSliceInstruction', 'Slice the nouns 🔪 — avoid everything else!')}
            </p>

            <div
              ref={containerRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onTouchStart={(e) => { const tt = e.touches[0]; if (tt) trySliceAt(tt.clientX, tt.clientY); }}
              onTouchMove={handleTouchMove}
              className="relative min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-sky-50 to-white"
              style={{ touchAction: 'none', cursor: 'crosshair' }}
            >
              {slowMo && (
                <>
                  <div className="pointer-events-none absolute inset-0 bg-indigo-500/[0.06] transition-opacity duration-200" />
                  <div className="pointer-events-none absolute left-0 right-0 top-2 flex justify-center">
                    <span className="rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-black text-white">
                      {t('games.fruitSliceSlowMo', 'SLOW-MO')}
                    </span>
                  </div>
                </>
              )}

              {fruitsRef.current.map((f) => {
                if (f.resolved) return null;
                const pos = positionOf(f, elapsedMsRef.current, playSize.width, playSize.height);
                return (
                  <div
                    key={f.id}
                    className="pointer-events-none absolute flex w-[72px] -translate-x-1/2 -translate-y-1/2 flex-col items-center"
                    style={{ left: pos.x, top: pos.y }}
                  >
                    <span className="text-4xl leading-none">{f.emoji}</span>
                    <span
                      dir={targetLang.dir}
                      className="mt-0.5 max-w-full truncate rounded-lg bg-black/60 px-2 py-0.5 text-[11px] font-extrabold text-white"
                    >
                      {f.source.word}
                    </span>
                  </div>
                );
              })}

              {particles.map((p) => (
                <span
                  key={p.id}
                  className="pointer-events-none absolute h-4 w-4 rounded-full game-particle-pop"
                  style={{
                    left: p.x - 8,
                    top: p.y - 8,
                    background: p.success ? '#58CC02' : '#FF4B4B',
                  }}
                />
              ))}

              {trailRect && trail.map((pt, i) => (
                <span
                  key={pt.id}
                  className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/70"
                  style={{
                    left: pt.x - trailRect.left,
                    top: pt.y - trailRect.top,
                    opacity: ((i + 1) / trail.length) * 0.6,
                    boxShadow: '0 0 6px rgba(255,255,255,0.8)',
                  }}
                />
              ))}

              <style>{`
                @keyframes game-particle-pop-kf {
                  0% { transform: scale(0.4); opacity: 0.9; }
                  100% { transform: scale(3); opacity: 0; }
                }
                .game-particle-pop { animation: game-particle-pop-kf 450ms ease-out forwards; }
              `}</style>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
